/**
 * Log a single health measurement (weight, HRV, steps, …). Used to live
 * inline at the top of the Measurements screen — moved into a modal so the
 * landing view is "what's my trend" rather than "log a number". Data
 * mostly arrives by import / agent sync; manual entry is the exception.
 */

import { useEffect, useState } from 'react';
import { useCreateMeasurement } from '../api/health';
import { FormModal } from './FormModal';
import styles from './FormModal.module.css';

interface MeasurementFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional preset type + unit, used when the user drilled into a chart. */
  preset?: { type: string; unit: string | null } | null | undefined;
}

/** The "quick-pick" buttons shown above the type input. Keep in sync with
 *  HealthMeasurements' catalog where it makes sense, but this is the
 *  short list — anything else is typed by hand. */
const COMMON_TYPES: ReadonlyArray<readonly [string, string, string]> = [
  ['weight', 'Weight', 'kg'],
  ['body_fat', 'Body fat', '%'],
  ['skeletal_muscle', 'Skeletal muscle', '%'],
  ['resting_hr', 'Resting HR', 'bpm'],
  ['hrv', 'HRV', 'ms'],
  ['vo2max', 'VO₂ max', 'ml/kg/min'],
  ['blood_oxygen', 'Blood oxygen', '%'],
  ['steps', 'Steps', ''],
  ['waist', 'Waist', 'cm'],
  ['waist_navel', 'Waist (navel)', 'cm'],
  ['hip', 'Hip', 'cm'],
  ['chest', 'Chest', 'cm'],
  ['thigh', 'Thigh', 'cm'],
  ['arm_relaxed', 'Arm (relaxed)', 'cm'],
  ['shoulders', 'Shoulders', 'cm'],
];

export function MeasurementFormModal({ open, onClose, preset }: MeasurementFormModalProps) {
  const create = useCreateMeasurement();

  const [type, setType] = useState<string>(preset?.type ?? 'weight');
  const [unit, setUnit] = useState<string>(preset?.unit ?? 'kg');
  const [value, setValue] = useState<string>('');
  const [recordedAt, setRecordedAt] = useState<string>(
    () => new Date().toISOString().slice(0, 16),
  );
  const [notes, setNotes] = useState<string>('');

  // Reset on open. We pick up `preset` so opening "+ Log" from a focused
  // metric pre-fills the type the user is already looking at.
  useEffect(() => {
    if (!open) return;
    setType(preset?.type ?? 'weight');
    setUnit(preset?.unit ?? (preset?.type === 'steps' ? '' : 'kg'));
    setValue('');
    setRecordedAt(new Date().toISOString().slice(0, 16));
    setNotes('');
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preset?.type]);

  const pickPreset = (t: (typeof COMMON_TYPES)[number]) => {
    setType(t[0]);
    setUnit(t[2]);
  };

  const num = Number(value);
  const valid = type.trim().length > 0 && value.trim() !== '' && Number.isFinite(num);
  const pending = create.isPending;
  const error = (create.error as Error | null)?.message ?? null;

  const submit = () => {
    if (!valid || pending) return;
    const input: {
      type: string;
      value: number;
      unit: string | null;
      notes: string;
      recordedAt?: string;
    } = {
      type: type.trim(),
      value: num,
      unit: unit.trim() || null,
      notes,
    };
    if (recordedAt) input.recordedAt = new Date(recordedAt).toISOString();
    create.mutate(input, { onSuccess: () => onClose() });
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title="Log measurement"
      subtitle="One reading. Type, value, unit, when."
      submitLabel={pending ? 'Logging…' : 'Log measurement'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
    >
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Quick pick</span>
        <div className={styles.pills}>
          {COMMON_TYPES.map((t) => (
            <button
              type="button"
              key={t[0]}
              className={`${styles.pill} ${type === t[0] ? styles.pillActive : ''}`}
              onClick={() => pickPreset(t)}
            >
              {t[1]}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="meas-type">Type</label>
        <input
          id="meas-type"
          className={styles.input}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="meas-value">Value</label>
        <input
          id="meas-value"
          className={styles.input}
          value={value}
          inputMode="decimal"
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. 84.2"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="meas-unit">Unit</label>
        <input
          id="meas-unit"
          className={styles.input}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          placeholder="kg, %, bpm, …"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="meas-when">When</label>
        <input
          id="meas-when"
          className={styles.input}
          type="datetime-local"
          value={recordedAt}
          onChange={(e) => setRecordedAt(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="meas-notes">Notes</label>
        <input
          id="meas-notes"
          className={styles.input}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </FormModal>
  );
}
